/**
 * The dojo's own negative controls and URL flags: every `--inject` a headless
 * check throws to prove it would have caught the pre-fix behaviour, plus the
 * handful of URL-only reads (`?walkfrozen=1` and the like) nothing else in
 * the closure computes. Each one is read or flipped from outside through
 * `state`, since the probe and the dojo checks both reach these by name.
 */

import * as THREE from 'three';

import { urlFlag, TEST_SWITCHES } from '../urlFlags.ts';

export interface InjectsState {
  /** `?groundedreturnold=1`: the hand-back as it was, with the walkaround left
   *  mounted and its own frame still owning the air once cruise draws.
   *  `grounded-handoff-check.mjs --inject grounded-return-old` reaches it. */
  readonly GROUNDED_RETURN_OLD: boolean;
  /** `?lowflightold=1`: cruise back to drawing the globe map alone, with no
   *  pyramid under a ship flying low.
   *  `grounded-view-check.mjs --inject low-flight-old` reaches it. */
  readonly LOW_FLIGHT_OLD: boolean;
  /** `?cruisedatumgate=1`: cruise arms the pyramid off the ship's height over
   *  the datum alone, so a ship low over Luna's highlands never streams it.
   *  `highland-tiles-check.mjs --inject datum-gate` reaches it. */
  readonly CRUISE_DATUM_GATE: boolean;
  /** `?atlasclocktwice=1`: the plot's tiers advance the clock a second time a
   *  frame, as they did. `atlas-clock-check.mjs --inject clock-twice` reaches it. */
  readonly CLOCK_TWICE: boolean;
  /** `?daybadgeold=1`: the canvas badge redrawn only with the overlay, as it
   *  was; `?daybadgeeveryframe=1`: rewritten on every frame.
   *  `atlas-clock-check.mjs --leg badge` reaches both. */
  readonly DAY_BADGE_OLD: boolean;
  readonly DAY_BADGE_EVERY_FRAME: boolean;
  /** `?scrubmovesclock=1`: the season and sol controls scrub the clock the ship
   *  flies on, as they did. `atlas-clock-check.mjs --inject scrub-old` reaches it. */
  readonly SCRUB_MOVES_CLOCK: boolean;
  /** `?panelkeepspreview=1`: a slider preview outlives its hidden panel.
   *  `atlas-clock-check.mjs --inject panel-keeps-preview` reaches it. */
  readonly PANEL_KEEPS_PREVIEW: boolean;
  /** `?warprowtiergate=1`: the warp row at the orrery rungs only; `?roverwarpdt=1`: the rover stepped on the clock's warped seconds;
   *  `?sunrisenoon=1`: SUNRISE aimed at local noon. */
  readonly WARP_ROW_TIER_GATE: boolean;
  readonly ROVER_WARP_DT: boolean;
  readonly SUNRISE_NOON: boolean;
  /** `?stalefocusground=1`: a jump's new anchor stands on the last anchor's
   *  ground until a leaf under it is read, as it did.
   *  `best-hex-check.mjs --inject stale-ground` reaches it. */
  readonly STALE_FOCUS_GROUND: boolean;
  /** `?zoomdropclimbs=1`: ground under the focus point that reads lower during a zoom in
   *  is taken on a held eye, so the altitude climbs, as it did. `zoom-climb-check.mjs` reaches it. */
  readonly ZOOM_DROP_CLIMBS: boolean;
  /** `?jumpkeepspatch=1`: a jump on the same body keeps the last landing's
   *  patch, and the walkaround stays on it, as it did.
   *  `best-hex-check.mjs --inject keep-patch` reaches it. */
  readonly JUMP_KEEPS_PATCH: boolean;
  /** `?blindcover=1`: the ground under the eye reads as nothing drawn where only
   *  a split's retired cover draws it, so the eye's floor falls back to the datum,
   *  as it did. `site-zoom-check.mjs --inject blind-cover` reaches it. */
  readonly BLIND_COVER: boolean;
  /** `?zoomoutshort=1`: a wheel notch out moves 0.8 of a notch, so zooming out stops
   *  short of where zooming in started. `site-zoom-check.mjs --inject zoom-out-short` reaches it. */
  readonly ZOOM_OUT_SHORT: boolean;
  /** `?standeaseoff=1`: a wheel notch inside the ground ease a goTo into SURFACE started holds the eye there,
   *  as it did. `site-zoom-check.mjs --stand-notch --inject stand-ease-off` reaches it. */
  readonly STAND_EASE_OFF: boolean;
  /** `?zoomkeepspitch=1`: a wheel notch pins the pitch first, so zooming out leaves it where zooming in
   *  clamped it. `site-zoom-check.mjs --inject keep-pitch` reaches it. */
  readonly ZOOM_KEEPS_PITCH: boolean;
  /** `?fitkeepspitch=1`: the opening fit leaves the pitch a site armed, so the first notch at the whole-system view
   *  tips it to that pitch. `site-zoom-check.mjs --pitch --inject fit-keeps-pitch` reaches it. */
  readonly FIT_KEEPS_PITCH: boolean;

  /** `descent-dojo --shipmark --inject ship-nearest`: the marker out of the pick path. */
  shipPickOff: boolean;
  /** `descent-dojo --shape --inject sphere-globe`: the undisplaced datum sphere. */
  shapeSphereDebug: boolean;
  /** `descent-dojo --arrival --inject shape-late`: a shaped body draws as its
   *  sphere until its shape lands, as it did. */
  shapeLateSwap: boolean;
  /** `descent-dojo --arrival --inject sphere-standin`: a shaped body waits for
   *  its shape as its sphere under the timed hold, not as its ellipsoid. */
  shapeRoundStandIn: boolean;

  /**
   * Restore the pre-fix geometry: the anchor on the registry radius and the
   * pitch floor on the body's. The negative control both `surface-clamp --inject
   * legacy` and `farside-check --inject legacy` are validated against.
   */
  legacyGeometry: boolean;

  /**
   * Restore the pre-fix ascent: the plot mesh gated on the eased `descent`
   * value instead of on `surfaceRegime()`, so it stays drawn for ~2 s after
   * the camera has already switched away from the plot's rig.
   * `surface-clamp --ascent --inject legacy-fade` is validated against it.
   */
  legacyPlotFade: boolean;

  /**
   * `surface-clamp --ascent --inject grey-frame`: the reader's own positive
   * control. Arms a one-shot clear-only frame the instant the ascent crosses
   * out of SURFACE, so the ground-reading gate has a known uniform frame to
   * catch before it is trusted to read the app.
   */
  greyFrameInject: boolean;
  greyFramePending: boolean;

  /**
   * Counts `drawFrame()` calls, not `renderer.render()` calls: the SURFACE
   * band's far-field pass draws twice per frame (leaves hidden, then shown),
   * so a reader wrapping `renderer.render` sees two entries per frame there.
   * `surface-clamp --ascent`'s ground reading keys its collector on this so
   * it reads the frame `drawFrame()` finished, not the mid-frame bake pass.
   */
  frameTick: number;

  /**
   * Restore the pre-fix card: the wheel handler stops calling `renderOverlay`,
   * so the ALTITUDE / GROUND SAMPLE / BAND rows hold the value from the tier's
   * first frame. `surface-clamp --card --inject stale-card` is validated against it.
   */
  legacyCard: boolean;

  /**
   * Restore the pre-fix descent on a body with no site: a CENTRE anchor all the
   * way to the clamp, with no standing eye floor and no walkaround pose - the
   * nadir view that filled the frame with one texel, and the drag that slid the
   * eye thousands of kilometres over the ground. `surface-clamp --frames
   * --inject centre-anchor` and `--stand --inject centre-anchor` are validated
   * against it.
   */
  legacyStanding: boolean;

  /** `descent-dojo --band-pop --inject siteless-stand`: a siteless body with relief stands on the leaves until its plot lands. */
  legacySitelessStand: boolean;

  /**
   * Restore the pre-fix landing point: derived once for the body and kept
   * across every ascent, so a descent from the far side converges on the last
   * one's ground. `surface-clamp --anchor --inject sticky-anchor`.
   */
  legacyStickyAnchor: boolean;

  /**
   * Read the camera's ground off the 256-sample FIELD again, the way it was
   * read before the mesh lattice: `surface-clamp --stand --inject field-height`.
   */
  legacyFieldHeight: boolean;

  // The claim's own controls, each also readable off the URL: a LAND press
  // starts the claim, so a switch thrown after the press comes too late.
  /**
   * `--ship --inject plot-edge-only`: the control for the SURFACE tick's own
   * ask below turned back into the one-shot it replaced. With this on, a
   * claim that lands after the crossing is never retried and the walkaround
   * waits forever - which is the wait `setPlotBuildDebug` puts back on purpose.
   */
  plotEdgeOnly: boolean;
  /**
   * `--walkaround --inject camera-on-datum`: the walkaround's eye floored on
   * the datum sphere instead of on the ground the plot draws.
   */
  readonly cameraOnDatum: boolean;
  /** `--walkaround --inject walk-frozen`: the movement keys reach nothing. */
  readonly walkFrozen: boolean;
  /** `plot-colour-check --inject tint-frozen`: the colour read at the build
   *  and never again, which `followPlotGroundColour` replaced. */
  readonly plotTintFrozen: boolean;
  /** `plot-colour-check --inject lowpass-flip`: the low-pass uploaded flipped,
   *  as the build's copy of three's default `flipY` left it. */
  readonly plotLowPassFlip: boolean;
  /** `grounded-handoff-check --inject tint-point`: the leaf's colour read as
   *  one downscaled pixel, which is a few texels near the middle, not a mean. */
  readonly plotTintPoint: boolean;
  /** `descent-dojo --band-pop --inject tint-mirror`: the leaf's colour read off
   *  the rectangle mirrored across its ancestor's middle, row 0 taken as v1. */
  readonly plotTintMirror: boolean;

  /** `--select --inject no-select-draw`: the selection held, nothing drawn. */
  selectDrawOff: boolean;
  /** `--select --inject wrong-cell`: a neighbour drawn where the pick landed. */
  selectWrongCell: boolean;
  /** `--select-enter --inject plot-off-centre`: ENTER PLOT back on the claim centre. */
  selectEnterOffCentre: boolean;
  /** `--select-cells --inject cells-mode-stuck`: the wash drawn whatever CELLS says. */
  cellsModeStuck: boolean;

  /** `descent-dojo --mover --inject wash-fade`: the fade starts at 0.12 of the extent. */
  washFadeInject: boolean;

  pickTierGate: boolean;
  /** `descent-dojo --hex --inject pick-facet`: the pre-fix triangle hit back. */
  pickFacetGate: boolean;
  /** `--hex --inject pick-datum`: the pick back on the datum sphere alone. */
  pickDatumGate: boolean;
  /** `descent-dojo --hex --inject hex-field-drape`: the height field back under
   *  the hex drape, in place of the geomorphed surface the tiles draw. */
  fieldDrapeDebug: boolean;
  /** `--stand --inject relief-datum-focus`: the focus anchor back on the datum under drawn relief. */
  reliefDatumFocus: boolean;
  /** `--stand --inject relief-datum-eye`: the eye's ground back on the datum under drawn relief. */
  reliefDatumEye: boolean;
  /** `--hex --inject hex-datum-drape`: the ORBIT drape back on the datum float. */
  datumDrapeDebug: boolean;
  /** `descent-dojo --hex --card --inject card-static`: freeze the card's own
   *  body/quad text, reproducing the fiction it used to always show. */
  cardStaticDebug: boolean;
  /** `descent-dojo --hex --claims --inject claim-circum`: the pre-fix plot raster. */
  claimCircumDebug: boolean;

  /** What `setColourDebug({ mistag })` changed, so it can be put back. */
  colourMistag: { texture: THREE.Texture; was: string } | null;

  /** `--reentry --inject rover-snap`: the pre-split behaviour, driving alone. */
  legacyRoverSnap: boolean;
}

export interface InjectsHandle {
  readonly state: InjectsState;
}

export function createInjects(): InjectsHandle {
  const state: InjectsState = {
    GROUNDED_RETURN_OLD: TEST_SWITCHES && urlFlag('groundedreturnold'),
    LOW_FLIGHT_OLD: TEST_SWITCHES && urlFlag('lowflightold'),
    CRUISE_DATUM_GATE: TEST_SWITCHES && urlFlag('cruisedatumgate'),
    CLOCK_TWICE: TEST_SWITCHES && urlFlag('atlasclocktwice'),
    DAY_BADGE_OLD: TEST_SWITCHES && urlFlag('daybadgeold'),
    DAY_BADGE_EVERY_FRAME: TEST_SWITCHES && urlFlag('daybadgeeveryframe'),
    SCRUB_MOVES_CLOCK: TEST_SWITCHES && urlFlag('scrubmovesclock'),
    PANEL_KEEPS_PREVIEW: TEST_SWITCHES && urlFlag('panelkeepspreview'),
    WARP_ROW_TIER_GATE: TEST_SWITCHES && urlFlag('warprowtiergate'),
    ROVER_WARP_DT: TEST_SWITCHES && urlFlag('roverwarpdt'),
    SUNRISE_NOON: TEST_SWITCHES && urlFlag('sunrisenoon'),
    STALE_FOCUS_GROUND: TEST_SWITCHES && urlFlag('stalefocusground'),
    ZOOM_DROP_CLIMBS: TEST_SWITCHES && urlFlag('zoomdropclimbs'),
    JUMP_KEEPS_PATCH: TEST_SWITCHES && urlFlag('jumpkeepspatch'),
    BLIND_COVER: TEST_SWITCHES && urlFlag('blindcover'),
    ZOOM_OUT_SHORT: TEST_SWITCHES && urlFlag('zoomoutshort'),
    STAND_EASE_OFF: TEST_SWITCHES && urlFlag('standeaseoff'),
    ZOOM_KEEPS_PITCH: TEST_SWITCHES && urlFlag('zoomkeepspitch'),
    FIT_KEEPS_PITCH: TEST_SWITCHES && urlFlag('fitkeepspitch'),

    shipPickOff: false,
    shapeSphereDebug: false,
    shapeLateSwap: false,
    shapeRoundStandIn: false,

    legacyGeometry: false,
    legacyPlotFade: false,
    greyFrameInject: false,
    greyFramePending: false,
    frameTick: 0,
    legacyCard: false,
    legacyStanding: false,
    legacySitelessStand: false,
    legacyStickyAnchor: false,
    legacyFieldHeight: false,

    plotEdgeOnly: TEST_SWITCHES && urlFlag('plotedgeonly'),
    cameraOnDatum: TEST_SWITCHES && urlFlag('cameradatum'),
    walkFrozen: TEST_SWITCHES && urlFlag('walkfrozen'),
    plotTintFrozen: TEST_SWITCHES && urlFlag('plottintfrozen'),
    plotLowPassFlip: TEST_SWITCHES && urlFlag('plotlowpassflip'),
    plotTintPoint: TEST_SWITCHES && urlFlag('plottintpoint'),
    plotTintMirror: TEST_SWITCHES && urlFlag('plottintmirror'),

    selectDrawOff: false,
    selectWrongCell: false,
    selectEnterOffCentre: false,
    cellsModeStuck: false,

    washFadeInject: false,

    pickTierGate: false,
    pickFacetGate: false,
    pickDatumGate: false,
    fieldDrapeDebug: false,
    reliefDatumFocus: false,
    reliefDatumEye: false,
    datumDrapeDebug: false,
    cardStaticDebug: false,
    claimCircumDebug: false,

    colourMistag: null,

    legacyRoverSnap: false,
  };

  return { state };
}
